<script lang="ts" setup>
    import {ref} from "vue";

    import DDivider from "../atoms/DDivider.vue";
    import DContainer from "../atoms/DContainer.vue";
    import DText from "../atoms/DText.vue";
    import DButton from "../atoms/DButton.vue";

    import type {BaseCardType} from "@songfinch/types";

    defineProps<
        BaseCardType & {
            accordionEntry: {
                title: string;
                content: string;
            }[];
        }
    >();

    const expandedIndex = ref(0);

    const toggleAccordion = (index: number) => {
        expandedIndex.value = expandedIndex.value === index ? -1 : index;
    };

    const isExpanded = (index: number) => {
        return expandedIndex.value === index;
    };
</script>

<template>
    <DContainer
        v-for="(entry, i) in accordionEntry"
        :key="entry.title"
        :data-test-id="dataTestId"
        class="d-accordion-entry"
        display="grid"
    >
        <DDivider spacing="small" class="d-accordion-divider"/>

        <DText
            variant="h-3"
            class="d-accordion-entry-title"
            @click="toggleAccordion(i)"
        >
            {{entry.title}}
        </DText>

        <Transition name="accordion">
            <DText
                v-if="isExpanded(i)"
                class="d-accordion-entry-content"
                v-html="entry.content"
            />
        </Transition>

        <DButton
            :data-test-id="`${dataTestId}-${isExpanded(i) ? 'expanded' : 'collapsed'}`"
            class="d-accordion-entry-button"
            :icon="isExpanded(i) ? 'remove_minus' : 'add_plus'"
            variant="icon"
            size="large"
            @click="toggleAccordion(i)"
        />

        <DDivider
            v-if="i + 1 === accordionEntry.length"
            spacing="small"
            class="d-accordion-divider"
        />
    </DContainer>
</template>

<style lang="scss" scoped>
.d-accordion-entry {
  width: 100%;
  max-width: 800px;
  grid-template-columns: 1fr 24px;
  column-gap: 1rem;
}

.d-accordion-entry-title {
  width: 100%;
  cursor: pointer;
  margin-bottom: 1rem;
  grid-column: 1;
  grid-row: 2;
}

.d-accordion-entry-content {
  grid-column: 1;
  /* transition: max-height 0.3s ease-in-out; */
}

.d-accordion-entry-button {
  grid-column: 2;
  grid-row: 2;
}

.d-accordion-divider {
  grid-column: span 2;
}

/* Transition styles */
.accordion-enter-active,
.accordion-leave-active {
  transition:
    max-height 0.3s ease-in-out,
    opacity 0.2s ease-in-out;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 300px; /* Adjust max height to your content size */
  opacity: 1;
}

/* Styling for the content wrapper */
.d-accordion-entry-content-wrapper {
  overflow: hidden;
  max-height: 0; /* For transition purposes */
}
</style>
